/**
 * iter 43 Phase 2 — Bulk "Proofs ready to send" inbox.
 *
 * Lists all proof_documents in status=ready for the current org, with a
 * bulk-select checkbox and a SEND SELECTED button that fires POST
 * /proofs/{id}/send in series. Recipient resolution is driven by the
 * customer's `proof_recipients_json` (set on the Customer drawer).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, formatErr } from "../lib/api";
import { Page } from "../components/AppShell";
import { PaperPlaneTilt, ArrowsClockwise } from "@phosphor-icons/react";

const STATUSES = ["ready", "pending_notary", "pending_render", "sent", "failed"];

export default function ProofsInbox() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("ready");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try { setItems((await api.get(`/proofs?status=${status}`)).data || []); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); setSelected({}); }, [status]); // eslint-disable-line

  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const allChecked = items.length > 0 && items.every(p => selected[p.id]);
  const someChecked = items.some(p => selected[p.id]);
  const checkAll = () => {
    if (allChecked) { setSelected({}); return; }
    const next = {};
    for (const p of items) next[p.id] = true;
    setSelected(next);
  };

  const sendSelected = async () => {
    const ids = items.filter(p => selected[p.id]).map(p => p.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Send ${ids.length} proof${ids.length === 1 ? "" : "s"} now?`)) return;
    setBusy(true); setErr("");
    let stubbed = 0;
    for (const id of ids) {
      try {
        const r = await api.post(`/proofs/${id}/send`, {});
        if (r.data?.stubbed) stubbed++;
      } catch (e) {
        setErr(formatErr(e));
        break;
      }
    }
    setBusy(false);
    if (stubbed > 0) {
      alert(`${ids.length} proof(s) marked as sent. ${stubbed} are stubbed — add SENDGRID_API_KEY to backend/.env to enable real email.`);
    }
    setSelected({});
    await load();
  };

  const reRender = async (id) => {
    try { await api.post(`/proofs/${id}/render`, {}); await load(); }
    catch (e) { setErr(formatErr(e)); }
  };

  return (
    <Page
      title="Proofs of Service"
      subtitle="BULK CLOSE-OUT"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Proofs"]}
      testid="proofs-inbox"
    >

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUSES.map(s => (
          <button
            key={s}
            type="button"
            className={`btn btn-sm ${status === s ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setStatus(s)}
            data-testid={`proofs-tab-${s}`}
          >{s.replace("_", " ").toUpperCase()}</button>
        ))}
        <button
          type="button"
          className="btn btn-ghost btn-sm ml-auto"
          onClick={load}
          data-testid="proofs-refresh"
        ><ArrowsClockwise size={12} /> REFRESH</button>
        {status === "ready" && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={!someChecked || busy}
            onClick={sendSelected}
            data-testid="proofs-send-selected"
          >
            <PaperPlaneTilt size={12} /> SEND SELECTED ({items.filter(p => selected[p.id]).length})
          </button>
        )}
      </div>

      {err && <div className="text-xs text-destructive mb-2">{err}</div>}

      <div className="surface">
        <table className="dense">
          <thead>
            <tr>
              {status === "ready" && (
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={checkAll}
                    data-testid="proofs-check-all"
                  />
                </th>
              )}
              <th>JOB</th>
              <th>STATUS</th>
              <th>NOTARY</th>
              <th>RECIPIENTS</th>
              <th>CREATED</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="7" className="text-center text-muted-foreground py-10" data-testid="proofs-empty">
                Nothing in this bucket.
              </td></tr>
            )}
            {items.map(p => (
              <tr key={p.id} data-testid={`proof-row-${p.id}`}>
                {status === "ready" && (
                  <td>
                    <input
                      type="checkbox"
                      checked={!!selected[p.id]}
                      onChange={() => toggle(p.id)}
                      data-testid={`proof-check-${p.id}`}
                    />
                  </td>
                )}
                <td>
                  <button
                    className="link-cell font-semibold"
                    onClick={() => navigate(`/m/proof/${p.id}`)}
                    data-testid={`proof-open-${p.id}`}
                  >Proof {p.id.slice(0, 8)}</button>
                </td>
                <td><span className="badge">{p.status.replace("_", " ")}</span></td>
                <td className="text-xs">
                  {p.notary_required ? (p.notary_signed_at ? "✓ signed" : "required") : "—"}
                </td>
                <td className="text-xs text-muted-foreground">
                  {p.sent_to_json
                    ? (typeof p.sent_to_json === "string" ? JSON.parse(p.sent_to_json) : p.sent_to_json).join(", ")
                    : "—"}
                </td>
                <td className="text-xs">{fmtDate(p.created_at)}</td>
                <td className="text-right">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => reRender(p.id)}
                    data-testid={`proof-render-${p.id}`}
                    title="Re-render PDF"
                  ><ArrowsClockwise size={12} /></button>
                  {p.pdf_url && (
                    <a className="btn btn-ghost btn-sm" href={p.pdf_url} target="_blank" rel="noreferrer" data-testid={`proof-pdf-${p.id}`}>
                      PDF ↗
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Page>
  );
}
